import React, { useMemo, useState, useCallback } from 'react';
import cn from 'clsx';
import {
  GalleryCardContent,
  Section,
  SelectionGroup,
  SelectionGroupValue,
  SelectionGroupOption,
  ButtonWithGAClickButton,
  PriceMediaCard,
  GalleryModal,
  Picture,
} from 'common/components';
import { analyticsUtils } from 'utils/analytics-utils';
import { CABINET_ELEMENT_BLOCK } from '../../cabinet-constants';
import { SELECTION_GROUP_LIST } from './cabinet-gallery-constants';
import { getImageList } from './cabinet-gallery-utils';
import {
  cabinetGalleryCardContent,
  cabinetGalleryFirstImageWrapper,
  cabinetGalleryFirstImageWrapperWithName,
  cabinetGalleryGrid,
  cabinetGalleryImage,
  cabinetGalleryImageWrapper,
  cabinetGalleryImageWrapperWithName,
  cabinetGallerySectionMoreBtn,
  cabinetGallerySectionTabs,
} from './cabinet-gallery-styles';
import { CabinetImageStructure } from './cabinet-gallery-types';

const INITIAL_IMAGES_PER_PAGE = 5;
const IMAGES_PER_PAGE = 6;

type CabinetGalleryProps = {
  signUpHref: string;
}

export const CabinetGallery: React.FC<CabinetGalleryProps> = ({ signUpHref }) => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [imagePerPage, setImagePerPage] = useState<number>(INITIAL_IMAGES_PER_PAGE);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const collectionList = useMemo(() => getImageList(activeTab), [activeTab]);

  const availableCollectionList = collectionList.slice(0, imagePerPage);
  const productsCount = collectionList.length;

  const handleTabChange = (e: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeTab) {
      setImagePerPage(INITIAL_IMAGES_PER_PAGE);
      setActiveTab(value as string);
    }

    const activeOption: SelectionGroupOption | undefined = SELECTION_GROUP_LIST.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent(
        `${CABINET_ELEMENT_BLOCK}`,
        activeOption.text,
        'Выберите свой стиль'
      );
    }
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen]);

  const handleCardClick = (position: number) => () => {
    setIsModalOpen(true);
    setActiveImageIndex(position);
    analyticsUtils.clickButtonEvent(
      CABINET_ELEMENT_BLOCK,
      collectionList[position].alt,
      'Галерея проектов',
      `${collectionList[position].alt} ${collectionList[position].addText}`
    );
  }

  const handleShowMoreBtnClick = () => {
    setImagePerPage(imagePerPage + IMAGES_PER_PAGE);
  };

  return (
    <Section>
      <SelectionGroup
        className={cabinetGallerySectionTabs}
        active={activeTab}
        onChange={handleTabChange}
        optionList={SELECTION_GROUP_LIST}
        rounded
      />

      <ul className={cabinetGalleryGrid}>
        {availableCollectionList.length > 0 && (
          availableCollectionList.map(({ name, price, src, srcSmall, alt, transformations }: CabinetImageStructure, index) => (
            <li onClick={handleCardClick(index)}>
              <PriceMediaCard>
                <div className={cn(
                  activeTab === 'all' ? cabinetGalleryImageWrapperWithName : cabinetGalleryImageWrapper,
                  { [cabinetGalleryFirstImageWrapperWithName]: index === 0 && activeTab === 'all' },
                  { [cabinetGalleryFirstImageWrapper]: index === 0 && activeTab !== 'all' }
                )}>
                  <Picture
                    alt={alt}
                    className={cabinetGalleryImage}
                    src={srcSmall ? srcSmall : src}
                    transformations={transformations}
                  />
                </div>
                <GalleryCardContent className={cabinetGalleryCardContent} displayedName={name} price={price} withCredit={false} />
              </PriceMediaCard>
            </li>
          )))}
      </ul>

      {imagePerPage <= productsCount && (
        <ButtonWithGAClickButton
          elementBlock={`${CABINET_ELEMENT_BLOCK} / Catalog`}
          elementName="Выберите свой стиль"
          elementText="Показать ещё"
          className={cabinetGallerySectionMoreBtn}
          rounded
          onClick={handleShowMoreBtnClick}
          adaptive={false}
        >
          Показать ещё
        </ButtonWithGAClickButton>
      )}

      <GalleryModal
        newAnalytics
        clickedImageIndex={activeImageIndex}
        elementBlock={CABINET_ELEMENT_BLOCK}
        elementText="Галерея проектов / modal"
        galleryList={collectionList}
        onClose={handleCloseModal}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            aria-label="Записаться на проектирование"
            elementBlock={CABINET_ELEMENT_BLOCK}
            elementName="Галерея проектов / modal"
            elementText="Записаться на проектирование"
            elementVariant={`${collectionList[activeImageIndex].alt} ${collectionList[activeImageIndex].addText}`}
            invert
            href={signUpHref}
            target="_blank"
            title="Записаться на проектирование"
            variant="accent"
          >
            Записаться на проектирование
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
        isShowImageName
        isShowImageAddText
      />
    </Section>
  );
}
